import { type HTMLAttributes, useCallback, useEffect, useState } from "react";

import {
  Listbox,
  ListboxButton,
  ListboxOption,
  ListboxOptions,
} from "@headlessui/react";

import { classes, Icon as SelectIcon } from "@alpacahq/ui/old";

type OptionType<T> = { value: T; label: string; subLabel?: string };

export interface SelectWithSublabelProps<T>
  extends Omit<HTMLAttributes<HTMLButtonElement>, "onChange" | "value"> {
  value: T;
  onChange: (value: T) => void;
  placeholder?: string;
  options: OptionType<T>[];
  callback?: (open: boolean) => void;
  label?: string;
}

export const SelectWithSublabel = <T,>({
  className,
  callback,
  value,
  onChange,
  placeholder = "Select an option",
  options,
  label,
  id,
}: SelectWithSublabelProps<T>) => {
  // function to find option by value
  const findOptionByValue = useCallback(
    (val: T) => options.find((option) => option.value === val),
    [options],
  );

  const [selectedOption, setSelectedOption] = useState<
    OptionType<T> | undefined
  >(() => (value ? findOptionByValue(value) : undefined));

  const handleSelectChange = useCallback(
    (selected: OptionType<T>) => {
      setSelectedOption(selected);
      onChange(selected.value);
    },
    [onChange],
  );

  // update selected option state when value prop changes
  // biome-ignore lint/correctness/useExhaustiveDependencies: options is the trigger: the selected option is re-resolved when the option list changes. The body only reads value and findOptionByValue.
  useEffect(() => {
    setSelectedOption(value ? findOptionByValue(value) : undefined);
  }, [options, value, findOptionByValue]);

  return (
    <div className="relative w-full">
      {label && (
        <label
          htmlFor={id}
          className="mb-1 block font-semibold text-gray-700 text-sm"
        >
          {label}
        </label>
      )}
      <Listbox value={selectedOption} onChange={handleSelectChange}>
        {({ open }) => {
          // pass the open state up to the parent component
          callback?.(open);

          return (
            <>
              <ListboxButton
                id={id}
                className={classes(
                  "relative flex min-h-9 w-full items-center whitespace-nowrap rounded border border-gray-400 bg-white px-2 font-semibold text-sm transition duration-200 ease-in-out hover:border-gray-500 focus:border-gray-500 focus:outline-none focus:ring-0 focus:ring-transparent dark:border-true-gray-500 dark:bg-true-gray-500",
                  className,
                )}
              >
                <div className="flex w-full items-center justify-between gap-1">
                  <div className="flex min-w-0 flex-1 items-center justify-between gap-1">
                    {selectedOption ? (
                      <>
                        <span className="font-medium text-gray-800 text-sm">
                          {selectedOption.label}
                        </span>
                        {selectedOption.subLabel && (
                          <span className="ml-auto truncate pl-4 font-normal text-gray-700 text-sm">
                            {selectedOption.subLabel}
                          </span>
                        )}
                      </>
                    ) : (
                      <span className="block truncate">{placeholder}</span>
                    )}
                  </div>
                  <div className="ml-auto flex shrink-0">
                    <SelectIcon name="ChevronDown" className="-my-2 h-4 w-4" />
                  </div>
                </div>
              </ListboxButton>
              {options.length > 0 && (
                <ListboxOptions
                  id="select-options"
                  className={classes(
                    "w-full min-w-fit",
                    "max-h-80 overflow-y-auto",
                    "absolute z-10 mt-2",
                    "border border-gray-400 bg-white shadow-[0_4px_8px_0_rgba(0,0,0,0.06)]",
                    "rounded-lg p-2 text-sm",
                  )}
                >
                  {options.map((option) => (
                    <ListboxOption key={String(option.value)} value={option}>
                      {({ selected }) => (
                        <div
                          className={classes(
                            "flex cursor-pointer items-center justify-between gap-1 rounded p-1.5",
                            "hover:bg-gray-50",
                            selected && "bg-[rgba(199,210,254,0.24)]",
                          )}
                        >
                          <span className="font-medium text-gray-800 text-sm">
                            {option.label}
                          </span>
                          {option.subLabel && (
                            <span className="ml-auto truncate font-normal text-gray-700 text-sm">
                              {option.subLabel}
                            </span>
                          )}
                        </div>
                      )}
                    </ListboxOption>
                  ))}
                </ListboxOptions>
              )}
            </>
          );
        }}
      </Listbox>
    </div>
  );
};
